import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import { usePostHog } from "../../../../hooks/usePostHog";
import { useAppStore } from "../../../../hooks/useStores";
import { Fibo2Person, Fibo2SceneObject, ImageDescription } from "../../../../models/image-description";
import { PlaygroundImage } from "../../../../models/image-to-image";
import { StructuredPrompt } from "../../../../models/structured-prompt";
import { TextToImageConfigType } from "../../../../models/text-to-image";
import { isFibo2App } from "../../../../utils";
import { ColorPickerPopover } from "./components/ColorPickerPopover";
import { EmbedAreaBox } from "./components/EmbedAreaBox";
import { EmbedAreaDrawLayer, MIN_EMBED_SIZE } from "./components/EmbedAreaDrawLayer";
import { ObjectBbox } from "./components/ObjectBbox";
import { ObjectDot } from "./components/ObjectDot";
import { ObjectInspectorPanel, TabValue } from "./components/ObjectInspectorPanel";
import { ObjectKeypoints } from "./components/ObjectKeypoints";
import { ResizeHandles } from "./components/ResizeHandles";
import { useBboxManipulation } from "./hooks/useBboxManipulation";
import { useRectManipulation } from "./hooks/useRectManipulation";
import styles from "./StructuredPromptImageCanvas.module.scss";
import { BoundingBox, CanvasObject } from "./types";
import { parseImageDescription } from "./utils/parseImageDescription";
import { parseStructuredPrompt } from "./utils/parseStructuredPrompt";

const DEBOUNCE_DELAY = 100;

interface StructuredPromptImageCanvasProps {
	image: PlaygroundImage;
	imageElement: HTMLImageElement | null;
}

const StructuredPromptImageCanvas: React.FC<StructuredPromptImageCanvasProps> = ({ image, imageElement }) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.canvas" });
	const { trackEvent } = usePostHog();
	const { fiboStore } = useAppStore();
	const { structuredPromptStore } = fiboStore;
	const {
		hoveredObjectId,
		setHoveredObjectId,
		selectedObjectId: syncedSelectedId,
		setSelectedObjectId: setSyncedSelectedId,
		showAllObjects,
		setIsCanvasHovered,
		isEmbedDrawActive,
		embedBbox,
		setEmbedBbox,
		embedSourceObjectId,
	} = structuredPromptStore;
	const [localObjects, setLocalObjects] = useState<CanvasObject[]>([]);
	const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
	// Lifted from ObjectInspectorPanel so ObjectKeypoints (sibling) can react to hover
	const [hoveredKeypointName, setHoveredKeypointName] = useState<string | null>(null);
	// Single source of truth for the inspector's active tab (shared with ObjectInspectorPanel).
	const [inspectorTab, setInspectorTab] = useState<TabValue>("colors");
	const isKeypointsTabActive = inspectorTab === "keypoints";
	const bboxUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const colorUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	// Pending debounced group-writes (keyed by edit type) so generate/refine can flush them.
	const pendingCommitsRef = useRef<Map<string, () => void>>(new Map());

	const config = image.config as TextToImageConfigType | undefined;
	const isFibo11 = config?.model === "fibo" && config?.model_id !== "fibo_lite" && config?.model_version !== "lite";
	const isFibo2 = config?.model_version === "FIBO_2" || isFibo2App();

	// The VGL tree has no way to reconstruct which layer + index a canvas-only "object-N" id refers
	// to (FIBO2 splits objects across three separate arrays sharing one global counter), so the
	// canvas <-> tree sync below uses this JSON path key ("foreground.1" / "objects.3") instead.
	const pathKeyOf = (obj: CanvasObject) => `${obj.layerKey ?? "objects"}.${obj.originalIndex}`;

	const selectedGroup = fiboStore.getSelectedFiboGroup();
	const groupStructuredPrompt = selectedGroup?.structured_prompt;

	const initialObjects = useMemo(() => {
		if (isFibo2 && groupStructuredPrompt) return parseImageDescription(groupStructuredPrompt);
		if (isFibo11 && groupStructuredPrompt) return parseStructuredPrompt(groupStructuredPrompt);
		return [];
	}, [isFibo2, isFibo11, groupStructuredPrompt]);

	useEffect(() => {
		setLocalObjects(initialObjects);
	}, [initialObjects]);

	// Sort objects by area (largest first) so smaller bboxes render on top
	const sortedObjects = useMemo(() => {
		return [...localObjects].sort((a, b) => {
			const areaA = a.bbox.width * a.bbox.height;
			const areaB = b.bbox.width * b.bbox.height;
			return areaB - areaA;
		});
	}, [localObjects]);

	useEffect(() => {
		return () => {
			if (bboxUpdateTimeoutRef.current) {
				clearTimeout(bboxUpdateTimeoutRef.current);
			}
			if (colorUpdateTimeoutRef.current) {
				clearTimeout(colorUpdateTimeoutRef.current);
			}
		};
	}, []);

	// Commit any pending debounced bbox/color writes immediately. Called by
	// fiboStore.flushVglEdits() before a generate/refine payload is built.
	const flushPendingCommits = useCallback(() => {
		if (bboxUpdateTimeoutRef.current) {
			clearTimeout(bboxUpdateTimeoutRef.current);
			bboxUpdateTimeoutRef.current = null;
		}
		if (colorUpdateTimeoutRef.current) {
			clearTimeout(colorUpdateTimeoutRef.current);
			colorUpdateTimeoutRef.current = null;
		}
		pendingCommitsRef.current.forEach((commit) => commit());
		pendingCommitsRef.current.clear();
	}, []);

	useEffect(() => fiboStore.registerVglFlush(flushPendingCommits), [fiboStore, flushPendingCommits]);

	// An embed area is only meaningful over the image it was drawn on, so it and its image
	// go when the canvas does.
	useEffect(() => {
		return () => structuredPromptStore.clearEmbedDraw();
	}, [structuredPromptStore]);

	const handleEmbedAreaCommit = useCallback(
		(bbox: BoundingBox) => {
			setEmbedBbox(bbox);
			structuredPromptStore.setIsEmbedDrawActive(false);
			trackEvent(POSTHOG_EVENTS.FIBO_EMBED_AREA_DRAWN, {
				width_percent: Math.round(bbox.width),
				height_percent: Math.round(bbox.height),
			});
		},
		[setEmbedBbox, structuredPromptStore, trackEvent],
	);

	const handleEmbedDrawCancel = useCallback(() => {
		structuredPromptStore.clearEmbedDraw();
		trackEvent(POSTHOG_EVENTS.FIBO_EMBED_DRAW_CANCELLED);
	}, [structuredPromptStore, trackEvent]);

	const handleEmbedFileChange = useCallback(
		(file: File | null) => {
			structuredPromptStore.setEmbedFile(file);
			if (file) trackEvent(POSTHOG_EVENTS.FIBO_EMBED_IMAGE_UPLOADED);
		},
		[structuredPromptStore, trackEvent],
	);

	useEffect(() => {
		if (!imageElement) return;

		const updateCanvasSize = () => {
			const rect = imageElement.getBoundingClientRect();
			setCanvasSize({ width: rect.width, height: rect.height });
		};

		updateCanvasSize();
		const resizeObserver = new ResizeObserver(updateCanvasSize);
		resizeObserver.observe(imageElement);
		return () => resizeObserver.disconnect();
	}, [imageElement]);

	// Reset keypoint state + tab (to Colors) when the selected object changes
	useEffect(() => {
		setHoveredKeypointName(null);
		setInspectorTab("colors");
	}, [syncedSelectedId]);

	const toPixels = (bbox: BoundingBox) => ({
		left: (bbox.left / 100) * canvasSize.width,
		top: (bbox.top / 100) * canvasSize.height,
		width: (bbox.width / 100) * canvasSize.width,
		height: (bbox.height / 100) * canvasSize.height,
	});

	const handleBboxUpdate = useCallback(
		(objectId: string, bbox: BoundingBox) => {
			const canvasObject = localObjects.find((obj) => obj.id === objectId);
			if (!canvasObject) return;

			setLocalObjects((prev) => prev.map((obj) => (obj.id === objectId ? { ...obj, bbox } : obj)));

			if (bboxUpdateTimeoutRef.current) {
				clearTimeout(bboxUpdateTimeoutRef.current);
			}

			const commit = () => {
				const currentGroup = fiboStore.getSelectedFiboGroup();
				if (!currentGroup?.structured_prompt) return;

				try {
					const originalIndex = canvasObject.originalIndex;

					if (canvasObject.layerKey !== null) {
						// FIBO 2 path — update within the appropriate layer
						const parsed: ImageDescription =
							typeof currentGroup.structured_prompt === "string"
								? JSON.parse(currentGroup.structured_prompt)
								: currentGroup.structured_prompt;

						const layerObjects = parsed[canvasObject.layerKey] as Fibo2SceneObject[];
						if (!layerObjects?.[originalIndex]?.bounding_boxes?.[0]) return;

						layerObjects[originalIndex].bounding_boxes![0] = {
							top_left: { x: bbox.left, y: bbox.top },
							bottom_right: { x: bbox.left + bbox.width, y: bbox.top + bbox.height },
						};

						fiboStore.updateFiboResultGroup(currentGroup.id, {
							structured_prompt: JSON.stringify(parsed),
						});
					} else {
						// BBQ / FIBO 1.1 path — flat objects array
						const parsed: StructuredPrompt =
							typeof currentGroup.structured_prompt === "string"
								? JSON.parse(currentGroup.structured_prompt)
								: currentGroup.structured_prompt;

						if (!parsed.objects?.[originalIndex]?.bounding_boxes?.[0]) return;

						parsed.objects[originalIndex].bounding_boxes![0] = {
							top_left: { x: bbox.left, y: bbox.top },
							bottom_right: { x: bbox.left + bbox.width, y: bbox.top + bbox.height },
						};

						fiboStore.updateFiboResultGroup(currentGroup.id, {
							structured_prompt: JSON.stringify(parsed),
						});
					}
				} catch (error) {
					console.error("Failed to update structured prompt bounding box:", error);
				}
			};

			pendingCommitsRef.current.set("bbox", commit);
			bboxUpdateTimeoutRef.current = setTimeout(() => {
				pendingCommitsRef.current.delete("bbox");
				commit();
			}, DEBOUNCE_DELAY);
		},
		[fiboStore, localObjects],
	);

	const handleColorChange = useCallback(
		(objectId: string, colorIndex: number, color: { r: number; g: number; b: number }) => {
			const canvasObject = localObjects.find((obj) => obj.id === objectId);
			if (!canvasObject) return;

			setLocalObjects((prev) =>
				prev.map((obj) =>
					obj.id === objectId
						? { ...obj, colors: obj.colors.map((c, i) => (i === colorIndex ? color : c)) }
						: obj,
				),
			);

			if (colorUpdateTimeoutRef.current) {
				clearTimeout(colorUpdateTimeoutRef.current);
			}

			const commit = () => {
				const currentGroup = fiboStore.getSelectedFiboGroup();
				if (!currentGroup?.structured_prompt) return;

				try {
					const originalIndex = canvasObject.originalIndex;

					if (canvasObject.layerKey !== null) {
						// FIBO 2 path
						const parsed: ImageDescription =
							typeof currentGroup.structured_prompt === "string"
								? JSON.parse(currentGroup.structured_prompt)
								: currentGroup.structured_prompt;

						const layerObjects = parsed[canvasObject.layerKey] as Fibo2SceneObject[];
						if (!layerObjects?.[originalIndex]?.colors?.[colorIndex]) return;

						layerObjects[originalIndex].colors![colorIndex] = {
							name: layerObjects[originalIndex].colors![colorIndex].name || "",
							red: color.r,
							green: color.g,
							blue: color.b,
						};

						fiboStore.updateFiboResultGroup(currentGroup.id, {
							structured_prompt: JSON.stringify(parsed),
						});
					} else {
						// BBQ / FIBO 1.1 path
						const parsed: StructuredPrompt =
							typeof currentGroup.structured_prompt === "string"
								? JSON.parse(currentGroup.structured_prompt)
								: currentGroup.structured_prompt;

						if (!parsed.objects?.[originalIndex]?.colors?.[colorIndex]) return;

						parsed.objects[originalIndex].colors![colorIndex] = {
							name: parsed.objects[originalIndex].colors![colorIndex].name || "",
							red: color.r,
							green: color.g,
							blue: color.b,
						};

						fiboStore.updateFiboResultGroup(currentGroup.id, {
							structured_prompt: JSON.stringify(parsed),
						});
					}
				} catch (error) {
					console.error("Failed to update structured prompt colors:", error);
				}
			};

			pendingCommitsRef.current.set("color", commit);
			colorUpdateTimeoutRef.current = setTimeout(() => {
				pendingCommitsRef.current.delete("color");
				commit();
			}, DEBOUNCE_DELAY);
		},
		[fiboStore, localObjects],
	);

	const handleAddColor = (objectId: string) => {
		const canvasObject = localObjects.find((obj) => obj.id === objectId);
		if (!canvasObject) return;

		const newColor = { r: 128, g: 128, b: 128 };
		setLocalObjects((prev) =>
			prev.map((obj) => (obj.id === objectId ? { ...obj, colors: [...obj.colors, newColor] } : obj)),
		);

		const currentGroup = fiboStore.getSelectedFiboGroup();
		if (!currentGroup?.structured_prompt) return;

		try {
			const originalIndex = canvasObject.originalIndex;

			if (canvasObject.layerKey !== null) {
				// FIBO 2 path
				const parsed: ImageDescription =
					typeof currentGroup.structured_prompt === "string"
						? JSON.parse(currentGroup.structured_prompt)
						: currentGroup.structured_prompt;

				const layerObjects = parsed[canvasObject.layerKey] as Fibo2SceneObject[];
				if (!layerObjects?.[originalIndex]) return;

				if (!layerObjects[originalIndex].colors) {
					layerObjects[originalIndex].colors = [];
				}
				layerObjects[originalIndex].colors!.push({
					name: "",
					red: newColor.r,
					green: newColor.g,
					blue: newColor.b,
				});

				fiboStore.updateFiboResultGroup(currentGroup.id, { structured_prompt: JSON.stringify(parsed) });
			} else {
				// BBQ / FIBO 1.1 path
				const parsed: StructuredPrompt =
					typeof currentGroup.structured_prompt === "string"
						? JSON.parse(currentGroup.structured_prompt)
						: currentGroup.structured_prompt;

				if (!parsed.objects?.[originalIndex]) return;

				if (!parsed.objects[originalIndex].colors) {
					parsed.objects[originalIndex].colors = [];
				}
				parsed.objects[originalIndex].colors!.push({
					name: "",
					red: newColor.r,
					green: newColor.g,
					blue: newColor.b,
				});

				fiboStore.updateFiboResultGroup(currentGroup.id, { structured_prompt: JSON.stringify(parsed) });
			}
		} catch (error) {
			console.error("Failed to add color to structured prompt:", error);
		}
	};

	const handleRemoveColor = (objectId: string, colorIndex: number) => {
		const canvasObject = localObjects.find((obj) => obj.id === objectId);
		if (!canvasObject) return;

		setLocalObjects((prev) =>
			prev.map((obj) =>
				obj.id === objectId ? { ...obj, colors: obj.colors.filter((_, i) => i !== colorIndex) } : obj,
			),
		);

		const currentGroup = fiboStore.getSelectedFiboGroup();
		if (!currentGroup?.structured_prompt) return;

		try {
			const originalIndex = canvasObject.originalIndex;

			if (canvasObject.layerKey !== null) {
				// FIBO 2 path
				const parsed: ImageDescription =
					typeof currentGroup.structured_prompt === "string"
						? JSON.parse(currentGroup.structured_prompt)
						: currentGroup.structured_prompt;

				const layerObjects = parsed[canvasObject.layerKey] as Fibo2SceneObject[];
				if (!layerObjects?.[originalIndex]?.colors) return;

				layerObjects[originalIndex].colors = layerObjects[originalIndex].colors!.filter(
					(_, i) => i !== colorIndex,
				);

				fiboStore.updateFiboResultGroup(currentGroup.id, { structured_prompt: JSON.stringify(parsed) });
			} else {
				// BBQ / FIBO 1.1 path
				const parsed: StructuredPrompt =
					typeof currentGroup.structured_prompt === "string"
						? JSON.parse(currentGroup.structured_prompt)
						: currentGroup.structured_prompt;

				if (!parsed.objects?.[originalIndex]?.colors) return;

				parsed.objects[originalIndex].colors = parsed.objects[originalIndex].colors!.filter(
					(_, i) => i !== colorIndex,
				);

				fiboStore.updateFiboResultGroup(currentGroup.id, { structured_prompt: JSON.stringify(parsed) });
			}
		} catch (error) {
			console.error("Failed to remove color from structured prompt:", error);
		}
	};

	const { selectedObjectId, setSelectedObjectId, handleMouseDown } = useBboxManipulation(
		localObjects,
		canvasSize,
		handleBboxUpdate,
	);

	const { handleMouseDown: handleEmbedAreaMouseDown } = useRectManipulation(
		embedBbox,
		canvasSize,
		setEmbedBbox,
		MIN_EMBED_SIZE,
	);

	// Selecting a scene object gives up the embed area: there is one target at a time, so
	// the last gesture wins (the reverse direction lives in startEmbedDraw/seedEmbedArea).
	useEffect(() => {
		if (selectedObjectId) structuredPromptStore.clearEmbedDraw();
	}, [selectedObjectId, structuredPromptStore]);

	const handleKeypointMove = useCallback(
		(keypointName: string, x: number, y: number) => {
			const canvasObject = localObjects.find((obj) => obj.id === selectedObjectId);
			if (!canvasObject?.keypoints || canvasObject.layerKey === null) return;

			setLocalObjects((prev) =>
				prev.map((obj) =>
					obj.id === selectedObjectId
						? {
								...obj,
								keypoints: obj.keypoints?.map((kp) =>
									kp.name === keypointName ? { ...kp, x, y } : kp,
								),
							}
						: obj,
				),
			);

			const currentGroup = fiboStore.getSelectedFiboGroup();
			if (!currentGroup?.structured_prompt) return;

			try {
				const parsed: ImageDescription =
					typeof currentGroup.structured_prompt === "string"
						? JSON.parse(currentGroup.structured_prompt)
						: currentGroup.structured_prompt;

				const layerObjects = parsed[canvasObject.layerKey] as Fibo2SceneObject[];
				const target = layerObjects?.[canvasObject.originalIndex] as Fibo2Person;
				if (!target?.keypoints) return;

				const kpIndex = target.keypoints.findIndex((kp) => kp.name === keypointName);
				if (kpIndex === -1) return;

				target.keypoints[kpIndex] = { name: keypointName, point: { x, y } };

				fiboStore.updateFiboResultGroup(currentGroup.id, {
					structured_prompt: JSON.stringify(parsed),
				});
			} catch (error) {
				console.error("Failed to update keypoint position:", error);
			}
		},
		[fiboStore, localObjects, selectedObjectId],
	);

	useEffect(() => {
		const obj = localObjects.find((o) => o.id === selectedObjectId);
		setSyncedSelectedId(obj ? pathKeyOf(obj) : null);
	}, [selectedObjectId, localObjects, setSyncedSelectedId]);

	useEffect(() => {
		const idForPath = syncedSelectedId
			? (localObjects.find((o) => pathKeyOf(o) === syncedSelectedId)?.id ?? null)
			: null;
		if (idForPath !== selectedObjectId) {
			setSelectedObjectId(idForPath);
		}
	}, [syncedSelectedId]);

	// Handle clicks outside the canvas to deselect bbox
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const isInsideCanvas = target.closest("[data-canvas-container]");
			const isColorPicker = target.closest("[data-color-picker]");
			const isMuiPopover = target.closest(".MuiPopover-root");
			// Editing the VGL panel (tree/JSON) must NOT deselect the canvas object,
			// otherwise the inspector + keypoint dots vanish while editing.
			const isVglEditor = target.closest("[data-vgl-editor]");

			if (!isInsideCanvas && !isColorPicker && !isMuiPopover && !isVglEditor && selectedObjectId) {
				setSelectedObjectId(null);
				setHoveredObjectId(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [selectedObjectId, setSelectedObjectId, setHoveredObjectId]);

	if ((!isFibo11 && !isFibo2) || canvasSize.width === 0) return null;
	// An object-less scene still needs the canvas mounted for "Embed Image" — the drawn
	// area is the only way to embed into a scene with nothing detected in it.
	if (localObjects.length === 0 && !isEmbedDrawActive && !embedBbox) return null;

	const selectedObject = localObjects.find((o) => o.id === selectedObjectId);
	const embedBboxPx = embedBbox ? toPixels(embedBbox) : null;

	return (
		<Box
			className={clsx(styles.canvas, { [styles.canvasEmbedDrawActive]: isEmbedDrawActive })}
			data-canvas-container
			onMouseOver={() => setIsCanvasHovered(true)}
			onMouseOut={() => setIsCanvasHovered(false)}
			onMouseLeave={() => {
				setHoveredObjectId(null);
				setIsCanvasHovered(false);
			}}
			sx={{ width: canvasSize.width, height: canvasSize.height }}
		>
			<Box className={styles.canvasBackdrop} />

			{/* Draw mode hides every object affordance so the user draws over a clean image. */}
			{!isEmbedDrawActive &&
				sortedObjects.map((obj) => {
					const isSelected = selectedObjectId === obj.id;
					const isHovered = hoveredObjectId === pathKeyOf(obj) || showAllObjects;
					const pxBbox = toPixels(obj.bbox);

					if (isSelected) {
						// Hide bbox/resize handles when Keypoints tab is active — only dots are visible
						if (isKeypointsTabActive) return null;
						return (
							<div key={obj.id} data-object-element>
								<ObjectBbox
									bbox={pxBbox}
									name={obj.name}
									state="selected"
									onMouseDown={(e) => handleMouseDown(e, obj.id)}
								/>
								<ResizeHandles
									bbox={pxBbox}
									onMouseDown={(e, handle) => handleMouseDown(e, obj.id, handle)}
								/>
							</div>
						);
					}

					// "Embed here" parks the embed area's "Add image" button on this object's
					// center — hide its dot for the embed's duration, like selection does.
					const isEmbedSource = pathKeyOf(obj) === embedSourceObjectId;

					return (
						<div key={obj.id} data-object-element>
							{isHovered && (
								<ObjectBbox
									bbox={pxBbox}
									name={obj.name}
									state="hover"
									onClick={(e) => {
										e.stopPropagation();
										setSelectedObjectId(obj.id);
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
										setSelectedObjectId(obj.id);
										handleMouseDown(e, obj.id);
									}}
								/>
							)}
							{!isHovered && !isEmbedSource && (
								<ObjectDot
									x={pxBbox.left + pxBbox.width / 2}
									y={pxBbox.top + pxBbox.height / 2}
									onMouseEnter={() => setHoveredObjectId(pathKeyOf(obj))}
									onClick={(e) => {
										e.stopPropagation();
										setSelectedObjectId(obj.id);
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
										setSelectedObjectId(obj.id);
										handleMouseDown(e, obj.id);
									}}
								/>
							)}
							<div
								className={clsx(styles.bboxHitbox, { [styles.bboxHitboxDisabled]: selectedObjectId })}
								style={{
									left: pxBbox.left,
									top: pxBbox.top,
									width: pxBbox.width,
									height: pxBbox.height,
								}}
								onMouseEnter={() => setHoveredObjectId(pathKeyOf(obj))}
								onClick={(e) => {
									e.stopPropagation();
									setSelectedObjectId(obj.id);
								}}
								onMouseDown={(e) => {
									e.stopPropagation();
									setSelectedObjectId(obj.id);
									handleMouseDown(e, obj.id);
								}}
							/>
						</div>
					);
				})}

			{isEmbedDrawActive && (
				<>
					<EmbedAreaDrawLayer
						canvasSize={canvasSize}
						onCommit={handleEmbedAreaCommit}
						onCancel={handleEmbedDrawCancel}
					/>
					<Box className={styles.embedHintPill}>
						<Typography className={styles.embedHintText}>{t("embedDrawHint")}</Typography>
						<BriaButton
							buttonType="textSmall"
							className={styles.embedHintCancel}
							onClick={handleEmbedDrawCancel}
						>
							{t("embedDrawCancel")}
						</BriaButton>
					</Box>
				</>
			)}

			{!isEmbedDrawActive && embedBbox && embedBboxPx && (
				<EmbedAreaBox
					bbox={embedBboxPx}
					file={structuredPromptStore.embedFile}
					onFileChange={handleEmbedFileChange}
					onMouseDown={handleEmbedAreaMouseDown}
					onRemove={handleEmbedDrawCancel}
				/>
			)}

			{selectedObject && !isFibo2 && !isEmbedDrawActive && (
				<ColorPickerPopover
					object={selectedObject}
					bbox={toPixels(selectedObject.bbox)}
					canvasSize={canvasSize}
					onColorChange={handleColorChange}
					onAddColor={handleAddColor}
					onRemoveColor={handleRemoveColor}
				/>
			)}

			{selectedObject && isFibo2 && !isEmbedDrawActive && (
				<>
					<ObjectInspectorPanel
						object={selectedObject}
						bbox={toPixels(selectedObject.bbox)}
						canvasSize={canvasSize}
						onColorChange={handleColorChange}
						onAddColor={handleAddColor}
						onRemoveColor={handleRemoveColor}
						hoveredKeypointName={hoveredKeypointName}
						onKeypointHover={setHoveredKeypointName}
						activeTab={inspectorTab}
						onTabChange={setInspectorTab}
						onEmbedHere={() =>
							selectedObject &&
							structuredPromptStore.seedEmbedArea(selectedObject.bbox, pathKeyOf(selectedObject))
						}
					/>
					{isKeypointsTabActive && !!selectedObject.keypoints?.length && (
						<ObjectKeypoints
							keypoints={selectedObject.keypoints}
							canvasSize={canvasSize}
							hoveredKeypointName={hoveredKeypointName}
							onKeypointMove={handleKeypointMove}
						/>
					)}
				</>
			)}
		</Box>
	);
};

export default observer(StructuredPromptImageCanvas);
